import React, { MutableRefObject, SyntheticEvent } from 'react';
import classNames from 'classnames';
import Avatar from '@components/Avatar';
import { PenIcon, CirclePenIcon, PlusIcon, XIcon } from '@components/Icons';
import styles from './ContactFormAvatar.module.scss';
import { AvatarConfig } from '..';

const ImageInput = ({ handleUpload }) => (
  <input
    className={styles.file}
    onChange={handleUpload}
    id="photo"
    type="file"
    name="file"
    accept=".png,.jpg,.gif,.jpeg"
  />
);

const filterDuplicates = (arr: Array<string>): Array<string> => Array.from(new Set(arr));

export default class ContactFormAvatar extends React.Component<{
  value?: string;
  avatar?: AvatarConfig;
  className?: string;
  displayName?: string;
  initials?: string;
  onChange: (url: string, file: File) => void;
  grayscale?: boolean;
}> {
  optionsContainerRef: MutableRefObject<HTMLDivElement>;
  timerID: MutableRefObject<any>;
  state = {
    fileUpload: null,
    editing: false,
    debouncedDisplayName: null,
  };

  constructor(props) {
    super(props);
    this.prepare();
    this.optionsContainerRef = React.createRef();
    this.timerID = React.createRef();
    this.state.debouncedDisplayName = props.displayName;
  }

  componentDidUpdate() {
    const displayName = this.props.displayName;
    if (displayName != this.state.debouncedDisplayName) {
      clearTimeout(this.timerID.current);
      this.timerID.current = setTimeout(
        () => this.setState({ debouncedDisplayName: displayName }),
        350,
      );
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timerID.current);
  }

  private selected(url, file = null) {
    this.setState({ editing: false });
    this.props.onChange(url, file);
  }

  handleSelect(url, e) {
    e.preventDefault();
    this.selected(url);
  }

  private prepare() {
    if (this.props.value || this.props.avatar?.options.length > 0) {
      return;
    }
  }

  handleUpload = (e: SyntheticEvent) => {
    const upload = e.target as HTMLInputElement;
    const file = upload.files[0];

    if (!file) {
      return;
    }

    const url = URL.createObjectURL(file);

    return this.setState(
      {
        fileUpload: url,
      },
      () => this.selected(url, file),
    );
  };

  handleEdit = (e: SyntheticEvent) => {
    e.preventDefault();
    this.optionsContainerRef.current?.focus?.();
    this.setState({ editing: true });
  };

  handleClose = (e: SyntheticEvent) => {
    e.preventDefault();
    this.setState({ editing: false });
  };

  render() {
    const { fileUpload, editing } = this.state;
    const avatar = this.props.avatar || ({} as AvatarConfig);
    const selected = this.props.value || avatar.value;
    const options = filterDuplicates(avatar.options || []);

    return (
      <div
        className={classNames(styles.container, this.props.className, editing && styles.editing)}
      >
        <span className={styles.active}>
          <label className={styles.selectedAvatar} onClick={this.handleEdit}>
            <Avatar
              size="lg"
              className={styles.avatar}
              url={selected}
              name={this.state.debouncedDisplayName}
              initials={this.props.initials}
              grayscale={this.props.grayscale}
            />
            <CirclePenIcon width={16} height={16} className={styles.penIcon} />
            <ImageInput handleUpload={this.handleUpload} />
          </label>
        </span>
        <div className={styles.optionsContainer}>
          <div className={styles.options} tabIndex={0} ref={this.optionsContainerRef}>
            <button
              onClick={this.handleClose}
              className={classNames(styles.option, styles.removeOption)}
            >
              <span className={classNames('avatarSelectBtn', styles.btn)}>
                <XIcon />
              </span>
            </button>
            {options.map((option, i) => (
              <a
                href="#"
                onClick={this.handleSelect.bind(this, option)}
                className={classNames(
                  styles.option,
                  selected === option ? styles.optionSelected : null,
                )}
                key={i}
              >
                <Avatar className={styles.optionAvatar} size="lg" url={option} />
              </a>
            ))}
            <label className={classNames(styles.option)}>
              <span className={classNames('avatarSelectBtn', styles.btn)}>
                {fileUpload && <Avatar className={styles.editAvatar} size="lg" url={fileUpload} />}
                {fileUpload ? <PenIcon className={styles.editIcon} /> : <PlusIcon />}
              </span>
              <ImageInput handleUpload={this.handleUpload} />
            </label>
          </div>
        </div>
      </div>
    );
  }
}
